import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { iconOf, unitsOf, allUnits, setUnit, stateOf, summarize } from '../../lib/permissions';
import { visibleNav } from '../../lib/nav';

export function Switch({ state, onChange, disabled, label, size = 'md' }) {
    const on = state === true;
    const some = state === 'some';
    const [w, h, k, travel] = size === 'sm' ? [30, 18, 12, 12] : [36, 20, 14, 16];
    return (
        <button
            type="button" role="switch" aria-checked={some ? 'mixed' : on} aria-label={label} title={label} disabled={disabled}
            onClick={(e) => { e.stopPropagation(); onChange(!on); }}
            style={{ width: w, height: h }}
            className={`inline-flex shrink-0 items-center rounded-full px-[3px] transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${on ? 'bg-blue-600' : some ? 'bg-amber-400' : 'bg-gray-300'}`}
        >
            <span
                style={{ width: k, height: k, transform: `translateX(${on ? travel : some ? travel / 2 : 0}px)` }}
                className="rounded-full bg-white shadow transition-transform"
            />
        </button>
    );
}

export default function PermissionPicker({ catalog, value, onChange, disabled, baseline }) {
    const units = new Set(value);
    const base = baseline ? new Set(baseline) : null;
    const groups = catalog.groups;
    const columns = catalog.actions;
    const [closed, setClosed] = useState(() => Object.fromEntries(groups.slice(1).map((g) => [g.key, true])));
    const emit = (next) => onChange([...next]);
    const setMany = (keys, on) => emit(keys.reduce((acc, k) => setUnit(catalog, acc, k, on), units));
    const all = allUnits(catalog);
    const granted = all.filter((u) => units.has(u)).length;

    const cell = (key, label) => {
        const change = base && units.has(key) !== base.has(key) ? (units.has(key) ? 'added' : 'removed') : null;
        return (
            <span
                className={`inline-flex rounded-full p-[3px] ${change === 'added' ? 'bg-green-50 ring-1 ring-green-600/40' : change === 'removed' ? 'bg-red-50 ring-1 ring-red-600/40' : ''}`}
                title={change ? `${label} — ${change === 'added' ? 'added for this person' : 'taken away from this person'}` : label}
            >
                <Switch state={units.has(key)} onChange={(on) => emit(setUnit(catalog, units, key, on))} disabled={disabled} label={label} size="sm" />
            </span>
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="font-semibold text-gray-900">{granted} of {all.length} permissions on</span>
                {base && (
                    <span className="flex items-center gap-3 text-gray-500">
                        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-green-600" />added</span>
                        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-600" />taken away</span>
                        <span>compared with the role</span>
                    </span>
                )}
                <span className="ml-auto flex gap-3 font-medium">
                    <button type="button" className="text-blue-600 hover:underline" onClick={() => setClosed({})}>Expand all</button>
                    <button type="button" className="text-blue-600 hover:underline" onClick={() => setClosed(Object.fromEntries(groups.map((g) => [g.key, true])))}>Collapse all</button>
                </span>
            </div>

            <div className="divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white">
                {groups.map((g) => {
                    const gUnits = g.modules.flatMap(unitsOf);
                    const on = gUnits.filter((u) => units.has(u)).length;
                    const open = !closed[g.key];
                    const GroupIcon = iconOf(g.icon);
                    const cols = columns.filter((a) => g.modules.some((m) => m.actions.includes(a.key)));
                    return (
                        <section key={g.key} className={`relative ${open ? 'bg-blue-50/40' : ''}`}>
                            {open && <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-blue-600" />}
                            <header
                                className="flex cursor-pointer select-none items-center gap-3.5 px-4 py-3.5 hover:bg-blue-50/40"
                                onClick={() => setClosed((c) => ({ ...c, [g.key]: open }))} aria-expanded={open}
                            >
                                <GroupIcon size={18} strokeWidth={1.75} className={`shrink-0 ${on ? 'text-blue-600' : 'text-gray-400'}`} />
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-semibold text-gray-900">{g.label}</span>
                                    <span className="block truncate text-sm text-gray-500">{g.modules.map((m) => m.label).join(', ')}</span>
                                </span>
                                <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium ${on === gUnits.length ? 'bg-green-100 text-green-800' : on ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                                    {on === gUnits.length ? 'All on' : on ? `${on} of ${gUnits.length}` : 'Off'}
                                </span>
                                <Switch state={stateOf(units, gUnits)} onChange={(v) => setMany(gUnits, v)} disabled={disabled} label={`All of ${g.label}`} />
                                <ChevronDown size={16} className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                            </header>
                            {open && (
                                <div className="overflow-x-auto border-t border-gray-200 bg-white">
                                    <table className="w-full min-w-[640px] text-left">
                                        <thead>
                                            <tr className="border-b border-gray-200 text-xs font-medium uppercase tracking-wider text-gray-500">
                                                <th className="px-4 py-2.5 font-medium">Module</th>
                                                {cols.map((a) => {
                                                    const keys = g.modules.filter((m) => m.actions.includes(a.key)).map((m) => `${m.key}:${a.key}`);
                                                    return (
                                                        <th key={a.key} className="w-[88px] px-2 py-2.5 text-center font-medium">
                                                            <span className="flex flex-col items-center gap-1.5">
                                                                {a.label}
                                                                {keys.length > 1 && (
                                                                    <Switch size="sm" state={stateOf(units, keys)} onChange={(v) => setMany(keys, v)} disabled={disabled} label={`${a.label} — every ${g.label} module`} />
                                                                )}
                                                            </span>
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {g.modules.map((m) => {
                                                const mine = unitsOf(m);
                                                const Icon = iconOf(m.icon);
                                                const extras = m.extras || [];
                                                return (
                                                    <tr key={m.key} className="align-top">
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-start gap-3">
                                                                <Switch state={stateOf(units, mine)} onChange={(v) => setMany(mine, v)} disabled={disabled} label={`All of ${m.label}`} />
                                                                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${units.has(`${m.key}:READ`) ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                                                                    <Icon size={16} strokeWidth={1.75} />
                                                                </span>
                                                                <span className="min-w-0">
                                                                    <span className="block text-sm font-medium text-gray-900">{m.label}</span>
                                                                    <span className="block text-xs text-gray-500">{m.hint}</span>
                                                                    {extras.length > 0 && (
                                                                        <span className="mt-2.5 flex flex-col gap-2">
                                                                            {extras.map((x) => (
                                                                                <span key={x.key} className="flex items-center gap-2.5" title={x.hint}>
                                                                                    {cell(x.key, x.label)}
                                                                                    <span className="text-sm text-gray-800">{x.label}</span>
                                                                                </span>
                                                                            ))}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        {cols.map((a) => (
                                                            <td key={a.key} className="px-2 py-3 text-center">
                                                                {m.actions.includes(a.key)
                                                                    ? cell(`${m.key}:${a.key}`, `${a.label} ${m.label}`)
                                                                    : <span className="text-gray-300">—</span>}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>
        </div>
    );
}

export function MenuPreview({ permissions }) {
    const granted = new Set(permissions);
    const sections = visibleNav((p) => granted.has(p));
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Their menu</p>
            {sections.length ? (
                <div className="mt-3 space-y-3">
                    {sections.map((s) => (
                        <div key={s.label}>
                            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{s.label}</p>
                            {s.items.map((n) => {
                                const Icon = n.icon;
                                return (
                                    <div key={n.id}>
                                        <p className="flex items-center gap-2 py-1 text-sm text-gray-700">
                                            {Icon && <Icon size={15} strokeWidth={1.75} className="text-gray-500" />}{n.label}
                                        </p>
                                        {n.children?.map((c) => (
                                            <p key={c.id} className="py-0.5 pl-6 text-xs text-gray-500">{c.label}</p>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            ) : <p className="mt-2 text-sm text-gray-500">Nothing yet — they would only see their own profile.</p>}
        </div>
    );
}

export function PermissionLine({ catalog, permissions = [], everything, own, shown = 4 }) {
    if (everything) return <span className="text-sm font-medium text-blue-600">Everything</span>;
    const names = summarize(catalog, permissions);
    return (
        <span className="text-sm text-gray-600" title={names.join(', ')}>
            {names.length ? names.slice(0, shown).join(', ') : 'Nothing yet'}
            {names.length > shown && <span className="text-gray-400"> +{names.length - shown} more</span>}
            {own && <span className="ml-2 text-xs font-medium text-amber-700" title="Set for this person — differs from their role">Own permissions</span>}
        </span>
    );
}
